import { useState, useRef, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { HiChevronLeft, HiChevronRight } from "react-icons/hi";
import AboutTab from "./about";
import ContactTab from "./Contact";
import ProjectsTab from "./Projects";
import SkillsTab from "./Skills";
import ExperienceTab from "./Experience";

const LandingTabs = () => {
  const tabs = { ABOUT: "ABOUT", EXPERIENCE: "EXPERIENCE", SKILLS: "SKILLS", PROJECTS: "PROJECTS", CONTACT: "CONTACT" };
  const [activeTab, setActiveTab] = useState(tabs.ABOUT);
  const scrollContainerRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  const checkArrows = useCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowLeftArrow(scrollLeft > 10);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  }, []);

  useEffect(() => {
    // Check arrows on mount and resize
    checkArrows();
    window.addEventListener('resize', checkArrows);
    return () => window.removeEventListener('resize', checkArrows);
  }, [checkArrows]);

  useEffect(() => {
    // Ensure active tab is visible when it changes (mobile only)
    if (window.innerWidth < 768 && scrollContainerRef.current) {
      const activeTabElement = scrollContainerRef.current.querySelector('[aria-current="page"]');
      if (activeTabElement && typeof activeTabElement.scrollIntoView === 'function') {
        setTimeout(() => {
          activeTabElement.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center'
          });
          setTimeout(() => checkArrows(), 300);
        }, 100);
      }
    }
  }, [activeTab, checkArrows]);
  
  const ACTIVE_TAB_CLASS =
    "inline-block px-6 py-3 text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-800 rounded-lg font-semibold border-2 border-gray-900 dark:border-white whitespace-nowrap";
  const IN_ACTIVE_TAB_CLASS =
    "inline-block px-6 py-3 rounded-lg hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200 border-2 border-transparent whitespace-nowrap";
  
  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 200;
      const newScrollLeft = scrollContainerRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
      scrollContainerRef.current.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
      
      // Update arrow visibility
      setTimeout(() => checkArrows(), 300);
    }
  };

  const handleTabClick = (tabLabel, event) => {
    setActiveTab(tabLabel);
    
    // Auto-scroll clicked tab into view on mobile
    if (window.innerWidth < 768 && scrollContainerRef.current && event.currentTarget) {
      setTimeout(() => {
        const tabElement = event.currentTarget;
        
        if (tabElement && typeof tabElement.scrollIntoView === 'function') {
          // Scroll the tab into view with proper padding
          tabElement.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center'
          });
          
          // Update arrows after scroll
          setTimeout(() => checkArrows(), 300);
        }
      }, 50);
    }
  };
  
  return (
    <>
      {/* Mobile Tabs with Arrows */}
      <div className="md:hidden mb-8 relative">
        {/* Left Arrow */}
        {showLeftArrow && (
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white dark:bg-gray-900 shadow-lg rounded-full p-2 border-2 border-gray-300 dark:border-gray-700"
            aria-label="Scroll left"
          >
            <HiChevronLeft className="text-2xl text-gray-900 dark:text-white" />
          </motion.button>
        )}
        
        {/* Scrollable Tabs Container */}
        <div 
          ref={scrollContainerRef}
          onScroll={checkArrows}
          className="overflow-x-auto scrollbar-hide px-12"
        >
          <ul className="flex gap-3 text-sm font-medium text-center text-gray-500 dark:text-gray-400 min-w-max pb-2">
            {Object.values(tabs).map((tabLabel, index) => (
              <motion.li 
                key={index}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span
                  aria-current={activeTab === tabLabel ? "page" : undefined}
                  className={`${
                    activeTab === tabLabel ? ACTIVE_TAB_CLASS : IN_ACTIVE_TAB_CLASS
                  } cursor-pointer`}
                  onClick={(e) => handleTabClick(tabLabel, e)}
                >
                  {tabLabel}
                </span>
              </motion.li>
            ))}
          </ul>
        </div>

        {/* Right Arrow */}
        {showRightArrow && (
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white dark:bg-gray-900 shadow-lg rounded-full p-2 border-2 border-gray-300 dark:border-gray-700"
            aria-label="Scroll right"
          >
            <HiChevronRight className="text-2xl text-gray-900 dark:text-white" />
          </motion.button>
        )}
      </div>

      {/* Desktop Tabs - No Arrows */}
      <div className="hidden md:block mb-8">
        <ul className="flex gap-3 text-sm font-medium text-center text-gray-500 dark:text-gray-400 justify-center pb-2">
          {Object.values(tabs).map((tabLabel, index) => (
            <motion.li 
              key={index}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <span
                aria-current={activeTab === tabLabel ? "page" : undefined}
                className={`${
                  activeTab === tabLabel ? ACTIVE_TAB_CLASS : IN_ACTIVE_TAB_CLASS
                } cursor-pointer`}
                onClick={(e) => handleTabClick(tabLabel, e)}
              >
                {tabLabel}
              </span>
            </motion.li>
          ))}
        </ul>
      </div>
      
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
      >
        {activeTab === tabs.ABOUT && <AboutTab />}
        {activeTab === tabs.EXPERIENCE && <ExperienceTab />}
        {activeTab === tabs.SKILLS && <SkillsTab />}
        {activeTab === tabs.PROJECTS && <ProjectsTab />}
        {activeTab === tabs.CONTACT && <ContactTab />}
      </motion.div>
    </>
  );
};

export default LandingTabs;
